// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@stoneage/App/Components/Common/XngZone";
import GridContainer, { GridContainerProps } from "@stoneage/MobiusOverrides/GridContainer/GridContainer";
import React, { FC } from "react";

import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

// XNG changes
type Props = OwnProps;

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Changes
    linkListGridItem?: GridItemProps;
    subscribeContainer?: InjectableCss;
    podcastContainer?: InjectableCss;
    footerBanner?: InjectableCss;
    linksWrapper?: InjectableCss;
    mzGridItem?: GridItemProps;
    footerWrapper?: InjectableCss;
    externalWrapper?: InjectableCss;
    topWrapper?: GridContainerProps;
    midWrapper?: GridContainerProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<Props> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const { isInShell, isEditing } = useShellContext();
    const isAuthenticated = useAppSelector(
        state =>
            (state.context.session.isAuthenticated || state.context.session.rememberMe) &&
            !state.context.session.isGuest,
    );

    // XNG-Changes:Change structure
    return (
        <StyledWrapper {...styles.footerWrapper} data-test-selector="FooterContainer">
            <StyledWrapper {...styles.topWrapper} data-test-selector="FooterContainer-Top">
                <GridContainer isFullWidth={true}>
                    <GridItem {...styles.mzGridItem}>
                        <XngZone zoneName="richContent" contentId={id} />
                    </GridItem>
                    <GridItem {...styles.subscribeGridItem}>
                        {(!isAuthenticated || isEditing) && (
                            <StyledWrapper
                                {...styles.subscribeContainer}
                                data-test-selector="FooterContainer-Subscribe"
                            >
                                <XngZone zoneName="subscribe" contentId={id} />
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.podcastContainer} data-test-selector="FooterContainer-Podcast">
                            <XngZone zoneName="podcast" contentId={id} />
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
            <StyledWrapper {...styles.midWrapper}>
                <GridContainer isFullWidth={true}>
                    <GridItem {...styles.linkListGridItem}>
                        <XngZone zoneName="LinkList1" contentId={id} />
                    </GridItem>
                    <GridItem {...styles.linkListGridItem}>
                        <XngZone zoneName="LinkList2" contentId={id} />
                    </GridItem>
                    <GridItem {...styles.linkListGridItem}>
                        <XngZone zoneName="LinkList3" contentId={id} />
                    </GridItem>
                </GridContainer>
                <StyledWrapper {...styles.linksWrapper}>
                    <XngZone zoneName="SocialLinks" contentId={id} normalWidth={true} />
                    <StyledWrapper {...styles.externalWrapper} {...{ isInShell }}>
                        <XngZone zoneName="Links" contentId={id} normalWidth={true} />
                        <XngZone zoneName="CopyRight" contentId={id} normalWidth={true} />
                        <XngZone zoneName="ReportBug" contentId={id} normalWidth={true} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
